import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  LayersControl,
  useMap
} from 'react-leaflet';
import { ChevronUp } from 'lucide-react';
// import MachineList from './MachineList';
import L from 'leaflet';
import { useNavigate } from 'react-router-dom';
import 'leaflet/dist/leaflet.css';

/* ------------------ Machine (Orange) Icon ------------------ */
const machineIcon = L.divIcon({
  className: '',
  html: `
    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 120 120">
      <circle cx="60" cy="60" r="40" fill="orange" stroke="white" stroke-width="10" />
    </svg>`,
  iconSize: [30, 30],
  iconAnchor: [15, 15],
});

/* ------------------ User (Blue) Icon ------------------ */
const userIcon = L.divIcon({
  className: '',
  html: `
    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 120 120">
      <circle cx="60" cy="60" r="40" fill=rgb(30,144,255) stroke="white" stroke-width="10" />
    </svg>`,
  iconSize: [30, 30],
  iconAnchor: [15, 15],
});

/* ------------------ MachineMarker Component ------------------ */
function MachineMarker({ machine, dataRef, onMarkerClick }) {
  const navigate = useNavigate();
  const markerRef = useRef(null);

  console.log(dataRef)

  if (!dataRef.current || !dataRef.current.LONGITUDE) {
    dataRef.current.LONGITUDE = 0
  }

  if (!dataRef.current || !dataRef.current.LATITUDE) {
    dataRef.current.LATITUDE = 0
  }

  const getMachineStatus = () => {
    const isCharging = Object.keys(dataRef.current)
  .filter(key => key.startsWith("IsCharging"))
  .some(key => dataRef.current[key] >= 1);

    const isRunning = dataRef.current.MotorSpeed > 150;

    if (isCharging) {
      return { status: 'Laden', icon: '⚡', color: 'text-yellow-500' };
    }
    if (isRunning) {
      return { status: 'Draait', icon: '●', color: 'text-green-500' };
    }
    return { status: 'Uit', icon: '●', color: 'text-gray-400' };
  };

  const getBatteryData = () => {
    return Object.keys(dataRef.current)
      .filter((key) => key.toLowerCase().startsWith('soc'))
      .map((key) => ({
        id: key.replace(/[^0-9]/g, ''), // extract numeric part to identify battery
        percentage: dataRef.current[key],
      }))
      .sort((a, b) => Number(a.id) - Number(b.id));
  };

  const formatDateTime = (timestamp) => {
    // timestamp is in seconds; multiply by 1000 for JS Date constructor
    const date = new Date(timestamp * 1000);
    const today = new Date();
    const isToday = date.toDateString() === today.toDateString();

    const timeString = date.toLocaleTimeString('nl-NL', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

    if (isToday) {
      return `vandaag, ${timeString}`;
    }

    return `${date.toLocaleDateString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    })}, ${timeString}`;
  };

  const { status, icon, color } = getMachineStatus();
  const batteries = getBatteryData();

  useEffect(() => {
    if (onMarkerClick) {
      onMarkerClick(markerRef);
    }
  }, [onMarkerClick]);

  return (
    <Marker
      ref={markerRef}
      position={[dataRef.current.LATITUDE, dataRef.current.LONGITUDE]}
      icon={machineIcon} // your custom icon
      eventHandlers={{
        click: () => {
          console.log('Marker clicked:', machine);
          markerRef.current?.openPopup();
        },
      }}
    >
      <Popup closeButton={false} className="request-popup">
        <div className="w-52">
          <div className="bg-white p-2">
            {/* Machine Info */}
            <div className="text-center space-y-0.5 mb-2">
              <h2 className="text-sm font-bold text-gray-900">{machine.alias}</h2>
              <div className="text-[11px] text-gray-600">
                {machine.brand} {machine.model}
              </div>
              <div className="text-[11px] text-gray-400">
                #{machine.serialNumber}
              </div>
              <p className="text-[10px] text-gray-400 mt-1">
                Laatst gezien: {formatDateTime(dataRef.current.GMT_UNIX)}
              </p>
            </div>

            {/* Status */}
            <div className="flex items-center justify-center gap-1.5 py-2 border-t border-b border-gray-100">
              <span className={`text-xs font-medium ${color}`}>{status}</span>
              <span className={`text-base ${color}`}>{icon}</span>
            </div>

            {/* Battery Section */}
            <div className="py-2">
              <div className="grid gap-1">
                {batteries.map(({ id, percentage }) => (
                  <div key={id} className="flex items-center gap-2">
                    <span className="text-xs text-gray-600 min-w-[55px]">
                      Batterij {id}
                    </span>
                    <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-green-500 rounded-full transition-all duration-300"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-gray-900 min-w-[32px] text-right">
                      {percentage}%
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Button */}
            <button
              onClick={() => {
                console.log('Navigating to dashboard for:', machine.serialNumber);
                navigate(`/machine/${machine.serialNumber}`);
              }}
              className="w-full bg-orange-500 hover:bg-orange-600 
                         text-white font-medium text-xs py-1.5 mt-1
                         transition-colors duration-200"
            >
              Ga naar dashboard
            </button>
          </div>
        </div>
      </Popup>
    </Marker>
  );
}

/* ------------------ UserLocationMarker Component ------------------ */
const UserLocationMarker = ({ userLocation }) => {
  if (!userLocation) return null;

  return (
    <Marker position={[userLocation.lat, userLocation.lng]} icon={userIcon}>
      <Popup>
        <div>You are here</div>
      </Popup>
    </Marker>
  );
};

function MachinesList({ machines, userLocation, onMachineClick }) {
  const map = useMap();
  const [isOpen, setIsOpen] = useState(false);
  const listRef = useRef(null);
  const touchStartY = useRef(null);

  useEffect(() => {
    if (isOpen) {
      map.dragging.disable();
      map.touchZoom.disable();
      map.doubleClickZoom.disable();
      map.scrollWheelZoom.disable();
      map.boxZoom.disable();
      map.keyboard.disable();
    } else {
      map.dragging.enable();
      map.touchZoom.enable();
      map.doubleClickZoom.enable();
      map.scrollWheelZoom.enable();
      map.boxZoom.enable();
      map.keyboard.enable();
    }
  }, [isOpen, map]);

  // Touch event handlers for smooth scrolling
  const handleTouchStart = (e) => {
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e) => {
    e.stopPropagation();
    if (!touchStartY.current) return;

    const currentY = e.touches[0].clientY;
    const diff = touchStartY.current - currentY;

    if (listRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = listRef.current;
      const isAtTop = scrollTop === 0;
      const isAtBottom = scrollHeight - scrollTop === clientHeight;

      if ((isAtTop && diff < 0) || (isAtBottom && diff > 0)) {
        e.preventDefault();
      }
    }
  };

  const handleTouchEnd = () => {
    touchStartY.current = null;
  };

  // Distance calculation helpers
  const getDistanceFromLatLonInKm = (lat1, lon1, lat2, lon2) => {
    const deg2rad = (deg) => deg * (Math.PI / 180);
    const R = 6371;
    const dLat = deg2rad(lat2 - lat1);
    const dLon = deg2rad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(deg2rad(lat1)) *
        Math.cos(deg2rad(lat2)) *
        Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const formatDistance = (distKm) =>
    distKm < 1
      ? `${(distKm * 1000).toFixed(0)}m`
      : `${distKm.toFixed(1)}km`;

  // Debugging: Log sorted machines
  const sortedMachines = useMemo(() => {
    if (!userLocation) {
      console.log("No user location, returning machines without sorting.");
      return machines;
    }

    const sorted = [...machines].sort((a, b) => {
        const distA = getDistanceFromLatLonInKm(
          userLocation.lat,
          userLocation.lng,
          a.liveData.LATITUDE,
          a.liveData.LONGITUDE
        );
        const distB = getDistanceFromLatLonInKm(
          userLocation.lat,
          userLocation.lng,
          b.liveData.LATITUDE,
          b.liveData.LONGITUDE
        );

        console.log(
            `Machine A: ${a.metadata.alias}, Distance: ${distA} km`,
            `Machine B: ${b.metadata.alias}, Distance: ${distB} km`
        );

        return distA - distB;
    });

    console.log("Sorted machines:", sorted);
    return sorted;
  }, [machines, userLocation]);

  // Handle machine click: fly to location and trigger parent callback
  const handleMachineClick = (machine) => {
    if (!machine || !machine.metadata || !machine.liveData) {
      console.error("Invalid machine object passed to MachinesList handleMachineClick:", machine);
      return;
    }

    const { LATITUDE, LONGITUDE } = machine.liveData;
    console.log("MachinesList - Machine clicked:", machine);

    // Trigger the parent callback
    onMachineClick(machine); // Pass the entire machine object

    // Zoom to the machine and open its popup
    if (LATITUDE) {
      map.flyTo([LATITUDE, LONGITUDE], 17, {
        animate: true,
        duration: 0.5, // Set this to a lower value (in seconds) for faster transitions
      });
    }

    setIsOpen(false); // Close the list
  };

  return (
    <div
      className={`fixed bottom-0 right-0 bg-white shadow-lg w-full sm:w-64 sm:bottom-4 sm:right-4 sm:rounded-lg transition-transform transform ${
        isOpen ? "translate-y-0" : "translate-y-[calc(100%-2rem)]"
      } sm:translate-y-0 touch-pan-y`}
      style={{ zIndex: 600 }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Pull Handle - Mobile Only */}
      <div
        className="flex justify-center items-center h-8 cursor-pointer sm:hidden"
        onClick={() => setIsOpen(!isOpen)}
      >
        <ChevronUp
          className={`w-6 h-6 text-orange-500 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </div>

      {/* Header */}
      <div className="sticky top-0 p-4 bg-gradient-to-r from-yellow-500 to-orange-600 sm:rounded-t-lg shadow-xs">
        <h4 className="text-white font-bold text-lg text-center m-0">
          Machines in de buurt
        </h4>
      </div>

      {/* Scrollable List */}
      <div
        ref={listRef}
        className="max-h-64 overflow-y-auto p-2 overscroll-contain z-50 scrollbar pr-0"
      >
        {sortedMachines.map((machine) => {
          const distance = userLocation
            ? formatDistance(
                getDistanceFromLatLonInKm(
                  userLocation.lat,
                  userLocation.lng,
                  machine.liveData.LATITUDE,
                  machine.liveData.LONGITUDE
                )
              )
            : null;

          // Debugging: Log distance and machine details
          console.log(
            `Rendering machine: ${machine.metadata.alias}`,
            `Distance: ${distance}`
          );

          return (
            <div
              key={machine.metadata.serialNumber}
              onClick={() => handleMachineClick(machine)}
              className="flex justify-between items-center p-4 cursor-pointer hover:bg-orange-50 border border-gray-200 rounded-lg mb-2 transition-colors duration-200"
            >
              <div>
                <span className="text-sm font-medium text-gray-800">
                  {machine.metadata.alias}
                </span>
                <div className="text-xs text-gray-500">{distance}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ------------------ GPSPanelWithLiveData Component ------------------ */
export default function GPSPanelWithLiveData() {
  const [liveData, setLiveData] = useState({});
  const [userLocation, setUserLocation] = useState(null);
  const mapRef = useRef(null);
  const dataRefs = useRef({});

  // Fetch live data every 5 seconds
  useEffect(() => {
    const fetchLiveData = () => {
      const storedData = localStorage.getItem('liveData');
      if (storedData) {
        try {
          const parsedData = JSON.parse(storedData);
          setLiveData((prev) => {
            if (JSON.stringify(prev) !== JSON.stringify(parsedData)) {
              Object.keys(parsedData).forEach((key) => {
                if (!dataRefs.current[key]) {
                  dataRefs.current[key] = { current: parsedData[key].liveData || {} };
                } else {
                  dataRefs.current[key].current = parsedData[key].liveData || {};
                }
              });
              return parsedData;
            }
            return prev;
          });
        } catch (error) {
          console.error('Error parsing liveData:', error);
        }
      }
    };

    fetchLiveData();
    const intervalId = setInterval(fetchLiveData, 5000);
    return () => clearInterval(intervalId);
  }, []);

  // Get user location
  useEffect(() => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => console.error('Error getting user location:', error)
      );
    }
  }, []);

  // Handle machine click
  const handleMachineClick = (machine) => {
    if (!mapRef.current) return;

    const { LATITUDE, LONGITUDE } = machine.liveData;
    console.log('Navigating to machine:', machine.metadata.alias);

    // Fly to machine and open popup
    mapRef.current.flyTo([LATITUDE, LONGITUDE], 17, {
      animate: true,
      duration: 0.5, // Set to a lower value for faster transitions
    });

    const markerRef = dataRefs.current[machine.metadata.serialNumber]?.ref;
    if (markerRef?.current) {
      markerRef.current.openPopup();
    }
  };

  const machinesWithLocation = useMemo(
    () =>
      Object.keys(liveData).map((key) => ({
        metadata: liveData[key].metadata,
        liveData: dataRefs.current[key]?.current || liveData[key].liveData,
      })),
    [liveData]
  );

  return (
    <div className="fixed inset-0">
      <MapContainer
        center={[52.1326, 5.2913]}
        zoom={7}
        style={{ height: '100%', width: '100%'}}
        whenCreated={(map) => (mapRef.current = map)}
        zoomControl={false} // Remove default zoom buttons
      >
        <LayersControl position="bottomleft">
          <LayersControl.BaseLayer name="Default View" checked>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Satellite">
            <TileLayer
              url="https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            />
          </LayersControl.BaseLayer>
        </LayersControl>

        {/* User Location Marker */}
        {userLocation && <UserLocationMarker userLocation={userLocation} />}

        {/* Machine Markers */}
        {machinesWithLocation.map((machine) => (
          <MachineMarker
            key={machine.metadata.serialNumber}
            machine={machine.metadata}
            dataRef={dataRefs.current[machine.metadata.serialNumber]}
            onMarkerClick={(markerRef) =>
              (dataRefs.current[machine.metadata.serialNumber] = { ...dataRefs.current[machine.metadata.serialNumber], ref: markerRef })
            }
          />
        ))}

        <MachinesList
          machines={machinesWithLocation}
          userLocation={userLocation}
          onMachineClick={handleMachineClick}
        />
      </MapContainer>
      {/*<MachineList machines={machinesWithLocation} />*/}
    </div>
  );
}
